import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Project } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Link, Switch, SwitchOnChangeData, Textarea, TextareaOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { onFormatDate } from '../Tools/converters';
import { useCustomers } from '../Tools/ObjectLoader';

export const ProjectEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    //const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const projectEdit = useSelector((state: ApplicationState) => state.data.projectEdit);
    const { customers } = useCustomers();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [displayName, setDisplayName] = React.useState(projectEdit ? projectEdit.displayName : "");
    const [actif, setActif] = React.useState<boolean>(projectEdit ? projectEdit.actif : true);
    const [customer, setCustomer] = React.useState<Customer | null | undefined>(customers ? customers.find((c) => projectEdit && c.customerid === projectEdit.customerid) : undefined);
    const [startDate, setStartDate] = React.useState<Date | null>(projectEdit && projectEdit.dateDebut ? new Date(projectEdit.dateDebut) : null);
    const [endDate, setEndDate] = React.useState<Date | null>(projectEdit && projectEdit.dateFin ? new Date(projectEdit.dateFin) : null);
    const [description, setDescription] = React.useState(projectEdit ? projectEdit.description : "");
    // Fields validation
    const [isDisplayNameValid, setDisplayNameValid] = React.useState(false);
    const [isCustomerValid, setCustomerValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);

    React.useEffect(() => {
        setDisplayNameValid(displayName !== undefined && displayName !== null && displayName.length > 0);
    }, [displayName]);
    React.useEffect(() => {
        setCustomerValid(customer !== null && customer !== undefined);
    }, [customer]);
    React.useEffect(() => {
        let submitValid = isDisplayNameValid && isCustomerValid;
        if (submitValid && projectEdit) {
            // If valid, check if any change on existing item
            submitValid = submitValid && (
                displayName !== projectEdit.displayName
                || actif !== projectEdit.actif
                || (customer !== null && customer !== undefined && customer.customerid !== projectEdit.customerid)
                || startDate !== projectEdit.dateDebut
                || endDate !== projectEdit.dateFin
                || description !== projectEdit.description
            );
        }
        setSubmitValid(submitValid);
    }, [displayName, isDisplayNameValid,
        actif,
        customer, isCustomerValid,
        startDate,
        endDate,
        description
    ]);

    const submit = () => {
        const c: Project = {
            projectid: projectEdit ? projectEdit.projectid : null,
            customerid: customer ? customer.customerid : null,
            displayName: displayName ? displayName : "",
            actif: actif,
            prixForfaitaire: null,
            dateDebut: ConvertionHelpers.DateToISODateOnly(startDate),
            dateFin: ConvertionHelpers.DateToISODateOnly(endDate),
            description: description,
            contacts: null
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                Saving project...
                            </ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Projects/" + (projectEdit && projectEdit.projectid !== null ? projectEdit.projectid : ""), {
                        method: projectEdit && projectEdit.projectid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Project saved
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<Project>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Project save failed
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) dispatch(dataActionCreators.patchProjectEdition(data));
                            dispatch(dataActionCreators.requestProjectEdition(null));
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{projectEdit && projectEdit.customerid !== null ? "Edit project " + projectEdit.displayName : "Create new project"}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestProjectEdition(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        <Field label={t('DisplayName')} required>
                            <Input
                                autoComplete="off"
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setDisplayName(data.value)}
                                value={displayName !== null ? displayName : ""}
                                maxLength={100}
                            />
                            {/*    error={!isDisplayNameValid}*/}
                        </Field>
                        <Field label={t('Customer')} className={styles.fluid} required>
                            <CustomerDropdown currentCustomer={customer ?? undefined}
                                customers={customers ?? []}
                                setCustomer={setCustomer} />
                        </Field>
                        <Field label="Description" className={styles.fluid}>
                            <Textarea resize="vertical"
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => { setDescription(data.value); }}
                                value={description ? description : undefined}
                            />
                        </Field>
                        <div className={styles.onSingleLine}>
                            <div className={styles.field50}>
                                <Field label={t('Start')} className={styles.fluid}
                                    required
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setStartDate(date === undefined ? null : date); }}
                                        value={startDate === null ? undefined : startDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectStartDate')}
                                    />
                                </Field>
                            </div>
                            <div className={styles.field50}>
                                <Field label={t('End')} className={styles.fluid}>
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setEndDate(date === undefined ? null : date); }}
                                        value={endDate === null ? undefined : endDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectEndDate')}
                                    />
                                </Field>
                            </div>
                        </div>
                        <Switch label="Actif project"
                            checked={actif}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setActif(data.checked); }} />

                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestProjectEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};